/**
 * Adaptive quality controller (P0-T06, SPEC-01 §4). Runs once per second of real
 * time; inputs are rafP95 (player-visible frame deltas), work p95 (scene frame
 * time) and refresh (median of shortest 10% of rAF deltas). Shed steps apply in
 * order and are never reversed in the session. Frozen by __KN.noAdapt = true.
 */
import type { Tier } from '@kota-nova/shared';
import { TIER_BUDGETS, TIER_SETTINGS } from '@kota-nova/shared';

export interface QualityInputs {
  rafP95: number;
  workP95: number;
  refresh: number;
}

export interface QualityTargets {
  /** current hardware scaling level (higher = lower resolution) */
  scale: number;
  applyScale(scale: number): void;
  /** the six shed steps, in order; return false if the step is unavailable */
  steps: (() => boolean)[];
  onShed(step: number): void;
}

export const SHED_STEP_NAMES = [
  'msaa→fxaa',
  'bloom off',
  'shadow half-rate',
  'crowd ×0.7',
  'radii ×0.8/0.85',
  'shadow distance ×0.7',
] as const;

export class QualityController {
  private slowStreak = 0;
  private fastStreak = 0;
  private lastTick = 0;
  private nextStep = 0;
  /** scale steps applied after shedding started (SPEC: only after hitting the floor do we go below 0.8x) */
  private shedScaleFloorReached = false;

  constructor(
    private readonly tier: Tier,
    private readonly targets: QualityTargets,
    private readonly dpr: number,
  ) {}

  get sMin(): number {
    return 1 / Math.min(this.dpr, TIER_SETTINGS[this.tier].maxDpr);
  }
  get soft(): number {
    return Math.min(TIER_SETTINGS[this.tier].maxScale, this.sMin / 0.8);
  }

  /** Called every rAF with the sampled inputs; evaluates once per second. */
  tick(nowMs: number, inputs: QualityInputs): void {
    if (nowMs - this.lastTick < 1000) return;
    this.lastTick = nowMs;
    this.evaluate(inputs);
  }

  /** Exposed for unit tests (R-02) and forced shedding (__KN.forceShed). */
  evaluate(inputs: QualityInputs): void {
    const target = TIER_BUDGETS[this.tier].p95FrameMs;
    const { rafP95, workP95, refresh } = inputs;
    const slow = rafP95 > Math.max(target, refresh) * 1.05;
    const fast = rafP95 <= 1.1 * refresh && workP95 < 0.7 * target;

    if (slow) {
      this.fastStreak = 0;
      this.slowStreak += 1;
      if (this.slowStreak >= 2) {
        // sustained 2 seconds
        if (this.targets.scale < this.soft) {
          this.setScale(Math.min(this.soft, this.targets.scale * 1.08));
        } else {
          if (!this.shedScaleFloorReached) this.shedScaleFloorReached = true;
          const shedOk = this.shed();
          if (!shedOk) {
            this.setScale(Math.min(TIER_SETTINGS[this.tier].maxScale, this.targets.scale * 1.08));
          }
        }
      }
    } else if (fast) {
      this.slowStreak = 0;
      this.fastStreak += 1;
      if (this.fastStreak >= 5 && this.targets.scale > this.sMin) {
        // recover sharpness (never reverses shed steps — only resolution)
        this.setScale(Math.max(this.sMin, this.targets.scale / 1.06));
      }
    } else {
      this.slowStreak = 0;
      this.fastStreak = 0;
    }
  }

  /** Apply the next shed step; true when one was applied. Never reversed. */
  shed(): boolean {
    while (this.nextStep < this.targets.steps.length) {
      const step = this.nextStep;
      this.nextStep += 1;
      const fn = this.targets.steps[step];
      if (fn && fn()) {
        this.targets.onShed(step);
        return true;
      }
    }
    return false;
  }

  get shedCount(): number {
    return this.nextStep;
  }

  private setScale(s: number): void {
    this.targets.applyScale(s);
  }
}
